import type {CSSProperties, ReactElement, JSX} from 'react'
import {isValidElement, cloneElement} from 'react'
import {clsx} from 'clsx'

export const QuoteBorderClasses = {
	primary: 'border-primary',
	secondary: 'border-secondary',
	galliprant: 'border-galliprant',
	galliprant_red: 'border-galliprant_red',
	bexacat_dark_grey: 'border-bexacat_dark_grey',
	varenzin_blue: 'border-varenzin_blue',
	varenzin_purple: 'border-varenzin_purple',
	trifexis: 'border-trifexis',
	micotil: 'border-micotil',
	interceptor_plus: 'border-interceptor_plus',
	capstar: 'border-capstar',
	credelio: 'border-credelio',
	bewormready: 'border-bewormready',
	elura: 'border-elura',
	entyce: 'border-entyce',
	comfortis: 'border-comfortis',
	advocate: 'border-advocate',
	experior: 'border-experior',
	zenrelia_primary: 'border-zenrelia_primary',
	zenrelia_secondary: 'border-zenrelia_secondary',
	zenrelia_contrast: 'border-zenrelia_contrast',
	zenrelia_accent: 'border-zenrelia_accent',
	zenrelia_sku1: 'border-zenrelia_sku1',
	zenrelia_sku2: 'border-zenrelia_sku2',
	zenrelia_sku3: 'border-zenrelia_sku3',
	zenrelia_sku4: 'border-zenrelia_sku4',
	credelio_quattro_guard_dog: 'border-credelio_quattro_guard_dog',
	credelio_quattro_hunter_green: 'border-credelio_quattro_hunter_green',
	credelio_quattro_grass_green: 'border-credelio_quattro_grass_green',
	credelio_quattro_tennis_ball_green:
		'border-credelio_quattro_tennis_ball_green',
	credelio_quattro_park_green: 'border-credelio_quattro_park_green',
	credelio_quattro_milk_bone: 'border-credelio_quattro_milk_bone',
	credelio_quattro_golden: 'border-credelio_quattro_golden',
	credelio_quattro_frisbee: 'border-credelio_quattro_frisbee',
	credelio_quattro_sunny_day: 'border-credelio_quattro_sunny_day',
	credelio_quattro_sky_blue: 'border-credelio_quattro_sky_blue',
	pet_protect_red: 'border-pet_protect_red',
	pet_protect_green: 'border-pet_protect_green',
	pet_protect_blue: 'border-pet_protect_blue',
	clean_up_2: 'border-clean_up_2',
	cydectin: 'border-cydectin',
	scour_bos: 'border-scour_bos',
	vira_shield: 'border-vira_shield',
	pet_protect_skin: 'border-pet_protect_skin',
	pet_protect_calm: 'border-pet_protect_calm',
	pet_protect_allergy: 'border-pet_protect_allergy',
	civ: 'border-civ',
	elmaro_navy_blue: 'border-elmaro_navy_blue',
	elmaro_pink: 'border-elmaro_pink',
	elmaro_yellow: 'border-elmaro_yellow',
	rosa_xo_verme: 'border-rosa_xo_verme',
	catosal_metabol: 'border-catosal_metabol',
	befrena_primary_blue: 'border-befrena_primary_blue',
	befrena_accent_green: 'border-befrena_accent_green',
	befrena_accent_pink: 'border-befrena_accent_pink',
	zolvix_plus_green: 'border-zolvix_plus_green',
	zolvix_plus_orange: 'border-zolvix_plus_orange',
	eluracat_dark_pink: 'border-eluracat_dark_pink',
	eluracat_light_pink: 'border-eluracat_light_pink',
	eluracat_yellow: 'border-eluracat_yellow',
	milbemax_red: 'border-milbemax_red',
	milbemax_blue: 'border-milbemax_blue',
}

export const ImageShapeClasses = {
	round: 'rounded-full h-44 w-44',
	square: 'h-44 w-auto',
	oval: 'h-44 w-64 rounded-full',
}

interface QuoteProps {
	fontClass?: string
	image: ReactElement<{
		className: string
		style?: CSSProperties
	}> | null
	quote: string
	quotee?: string
	showImageOnTop?: boolean
	borderColor?: keyof typeof QuoteBorderClasses | ''
	/** Hex color code for the border (takes priority over borderColor). Used by
	 *  the new color-picker flow (DCC-3661 / GWP-5210) because registry color IDs
	 *  are not all present in QuoteBorderClasses. Inline style is used so the
	 *  colour is not dependent on Tailwind safelisting. */
	borderHexColor?: string
	imageShape?: keyof typeof ImageShapeClasses
}

export const Quote = ({
	fontClass = 'text-xl',
	image,
	quote,
	quotee = '',
	showImageOnTop = false,
	borderColor = '',
	borderHexColor,
	imageShape = 'oval',
}: QuoteProps): JSX.Element => {
	const quoteFormatted = quote.split(' ')
	const quoteLastWord = quoteFormatted.pop()

	const imageClass = clsx(
		'mx-auto',
		ImageShapeClasses[imageShape],
		// Keep border width consistent at border-3 for both paths.
		// When a hex color is provided (new color picker), border-color is applied
		// via inline style below; the QuoteBorderClasses Tailwind colour is skipped.
		// Fall back to the legacy QuoteBorderClasses map for old CMS content.
		borderHexColor
			? 'border-3'
			: borderColor && `border-3 ${QuoteBorderClasses[borderColor]}`
	)

	return (
		<div
			className={clsx(
				'md:flex md:items-center',
				showImageOnTop && 'flex flex-col items-center'
			)}
		>
			{isValidElement(image)
				? cloneElement(image, {
						className: clsx(imageClass, image.props.className),
						// Merge with any existing inline styles on the image element so we
						// only override borderColor, not unrelated style properties.
						// Inline borderColor wins over Tailwind border-color utilities.
						...(borderHexColor
							? {
									style: {
										...image.props.style,
										borderColor: borderHexColor,
									},
								}
							: {}),
					})
				: image}

			<div
				className={
					!showImageOnTop && isValidElement(image) ? 'md:w-3/5' : ''
				}
			>
				<blockquote
					className={clsx('relative m-4 mb-8 pr-6', fontClass)}
				>
					<svg
						aria-hidden="true"
						className="absolute left-0 top-0 fill-current"
						focusable="false"
						height="23.3"
						viewBox="0 0 31.02 23.3"
						width="31.02"
						xmlns="http://www.w3.org/2000/svg"
					>
						<title>left-side-quote</title>
						<path
							d="M9.027,22.9V15.324H5a16.635,16.635,0,0,1,.171-2.355,6.984,6.984,0,0,1,.648-2.15A4.371,4.371,0,0,1,7.048,9.284,3.034,3.034,0,0,1,8.959,8.7V5.292a6.624,6.624,0,0,0-3.344.819A6.984,6.984,0,0,0,3.261,8.295,9.784,9.784,0,0,0,1.9,11.468a16.145,16.145,0,0,0-.444,3.856V22.9Zm12.489,0V15.324H17.49a16.634,16.634,0,0,1,.171-2.355,6.984,6.984,0,0,1,.648-2.15,4.371,4.371,0,0,1,1.228-1.536,3.034,3.034,0,0,1,1.911-.58V5.292a6.624,6.624,0,0,0-3.344.819A6.984,6.984,0,0,0,15.75,8.295a9.784,9.784,0,0,0-1.365,3.174,16.145,16.145,0,0,0-.444,3.856V22.9Z"
							transform="translate(-1.452 -5.292)"
						/>
					</svg>
					<span className="inline-block pl-12">
						{`${quoteFormatted.join(' ')} `}
						<span className="relative">
							{quoteLastWord}
							<svg
								aria-hidden="true"
								className="absolute right-0 fill-current"
								focusable="false"
								height="23.3"
								style={{
									right: '-60px',
									bottom: '-15px',
								}}
								viewBox="0 0 31.02 23.3"
								width="31.02"
								xmlns="http://www.w3.org/2000/svg"
							>
								<title>right-side-quote</title>
								<path
									d="M9.027,22.9V15.324H5a16.635,16.635,0,0,1,.171-2.355,6.984,6.984,0,0,1,.648-2.15A4.371,4.371,0,0,1,7.048,9.284,3.034,3.034,0,0,1,8.959,8.7V5.292a6.624,6.624,0,0,0-3.344.819A6.984,6.984,0,0,0,3.261,8.295,9.784,9.784,0,0,0,1.9,11.468a16.145,16.145,0,0,0-.444,3.856V22.9Zm12.489,0V15.324H17.49a16.634,16.634,0,0,1,.171-2.355,6.984,6.984,0,0,1,.648-2.15,4.371,4.371,0,0,1,1.228-1.536,3.034,3.034,0,0,1,1.911-.58V5.292a6.624,6.624,0,0,0-3.344.819A6.984,6.984,0,0,0,15.75,8.295a9.784,9.784,0,0,0-1.365,3.174,16.145,16.145,0,0,0-.444,3.856V22.9Z"
									transform="translate(21.517 22.9) rotate(180)"
								/>
							</svg>
						</span>
					</span>
				</blockquote>
				{quotee ? <p className="px-16">&mdash; {quotee}</p> : null}
			</div>
		</div>
	)
}
